/**
 * Sign in.
 *
 * Half the people who open this are standing in a car park on a phone in the
 * sun, so: large touch targets, a visible password toggle, no decorative
 * chrome between them and the two fields, and errors that say what to do.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TRADE_OPTIONS } from "../lib/trades";
import { Mark } from "../components/Mark";

export default function Login() {
  const [setup, setSetup] = useState<{ needs_setup: boolean; needs_token: boolean } | null>(null);

  // A fresh install has nobody to sign in as. It asks for its owner instead,
  // so nobody has to run a command to get in (CORE-2).
  useEffect(() => {
    api.setup
      .status()
      .then(setSetup)
      .catch(() => setSetup({ needs_setup: false, needs_token: false }));
  }, []);

  if (setup?.needs_setup) return <FirstRun needsToken={setup.needs_token} />;
  return <SignIn />;
}

function SignIn() {
  const { signIn, status, expired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const emailRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Where they were headed before the session ended.
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  useEffect(() => {
    if (status === "authenticated") navigate(from, { replace: true });
  }, [status, from, navigate]);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong signing in. Try again.",
      );
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <header className="login-head">
          <Mark size={34} />
          <div>
            <h1>Keystone</h1>
            <p>Field operations</p>
          </div>
        </header>

        {expired && (
          <p className="notice notice-warn" role="status">
            Your session ended. Sign in to pick up where you left off.
          </p>
        )}

        {error && (
          <p className="notice notice-crit" role="alert">
            {error}
          </p>
        )}

        <label className="field">
          <span>Email</span>
          <input
            ref={emailRef}
            id="login-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className="field">
          <span>Password</span>
          <div className="field-with-action">
            <input
              id="login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="link-button"
              onClick={() => setShowPassword((v) => !v)}
              aria-pressed={showPassword}
              tabIndex={-1}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p className="login-foot">
          Trouble signing in? Ask whoever set up your account to check your licence
          and access are still current.
        </p>
      </form>
    </main>
  );
}

/**
 * First run: the business and its owner, on an install with nobody in it.
 * The server allows this exactly once.
 */
function FirstRun({ needsToken }: { needsToken: boolean }) {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", full_name: "", email: "", password: "", setup_token: "", trade: "general", projects_enabled: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (form.password.length < 10) {
      setError("Choose a password of at least 10 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const token = await api.setup.create({
        business_name: form.business_name.trim(),
        trade: form.trade,
        projects_enabled: form.projects_enabled,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        setup_token: needsToken ? form.setup_token.trim() : undefined,
      });
      adopt(token, form.email);
      navigate("/settings", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't set up Keystone. Try again.");
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <header className="login-head">
          <Mark size={34} />
          <div>
            <h1>Set up Keystone</h1>
            <p>Nobody has signed in here yet. You'll be the owner.</p>
          </div>
        </header>
        {error && (
          <p className="notice notice-crit" role="alert">
            {error}
          </p>
        )}
        <label className="field">
          <span>Business name</span>
          <input value={form.business_name} onChange={set("business_name")} autoComplete="organization" required disabled={busy} />
        </label>
        <label className="field">
          <span>Trade</span>
          <select value={form.trade} disabled={busy} onChange={(e) => setForm(f => ({ ...f, trade: e.target.value }))}>
            {TRADE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className="field">
          <span><input type="checkbox" checked={form.projects_enabled} disabled={busy} onChange={e => setForm(f => ({ ...f, projects_enabled: e.target.checked }))} /> Add Projects for longer work</span>
          <small>Optional. Group jobs into project stages. You can turn this on later in Settings.</small>
        </label>
        <label className="field">
          <span>Your name</span>
          <input value={form.full_name} onChange={set("full_name")} autoComplete="name" required disabled={busy} />
        </label>
        <label className="field">
          <span>Your email — you'll sign in with it</span>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="username"
            autoCapitalize="none"
            required
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>Password (10 or more characters)</span>
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            autoComplete="new-password"
            required
            disabled={busy}
          />
        </label>
        {needsToken && (
          <label className="field">
            <span>Setup code</span>
            <input value={form.setup_token} onChange={set("setup_token")} autoComplete="off" disabled={busy} />
            <small className="tiny muted">The setup code supplied by the person hosting Keystone.</small>
          </label>
        )}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Setting up…" : "Create the business"}
        </button>
        <p className="login-foot">
          Next: your business details, then add your staff under Settings → People.
        </p>
      </form>
    </main>
  );
}
